import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../api";
import {
  MEASUREMENT_FIELDS, type Measurement, type MeasurementField, type Turbine,
} from "../types";
import { tg } from "../telegram";
import { ymd, fmtSlot } from "../utils/date";
import { useT } from "../i18n";

type TypeFilter = "" | "plan" | "emergency" | "offline";
type SortDir = "asc" | "desc";
type SortState = { key: string; dir: SortDir } | null;

// 4 columns that are always present (locked, can't be unchecked).
const FIXED_COLUMNS = [
  { key: "_date", label_key: "records.col_date" },
  { key: "_slot_time", label_key: "records.col_slot" },
  { key: "_type", label_key: "records.col_type" },
  { key: "_turbine", label_key: "records.col_turbine" },
] as const;

export default function MeasurementsCustomPreview() {
  const { t } = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // Period — initial from query string (passed from /measurements) or last 7d.
  const today = useMemo(() => ymd(new Date()), []);
  const sevenAgo = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() - 7); return ymd(d);
  }, []);
  const [from, setFrom] = useState<string>(params.get("from") || sevenAgo);
  const [to, setTo] = useState<string>(params.get("to") || today);

  // Filters
  const [turbineId, setTurbineId] = useState<number | null>(null);  // null = all
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("");
  const [turbines, setTurbines] = useState<Turbine[]>([]);

  // Field picker — Set of keys from MEASUREMENT_FIELDS
  const [chosenFields, setChosenFields] = useState<Set<string>>(new Set());

  // Generated table state
  const [items, setItems] = useState<Measurement[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<SortState>({ key: "_slot_time", dir: "desc" });

  // Ref on the <details> dropdown so we can collapse it after the table is built.
  const fieldsRef = useRef<HTMLDetailsElement | null>(null);

  useEffect(() => {
    api.get<Turbine[]>("/api/turbines").then(setTurbines).catch(() => undefined);
  }, []);

  const toggleField = (key: string) => {
    setChosenFields(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const selectAll = () => setChosenFields(new Set(MEASUREMENT_FIELDS.map(f => f.key)));
  const clearAll  = () => setChosenFields(new Set());

  const build = () => {
    setError(null);
    setLoading(true);
    setItems(null);
    // Collapse the field-picker dropdown so the table gets all the space.
    if (fieldsRef.current) fieldsRef.current.open = false;
    const q = new URLSearchParams({ from, to, limit: "2000" });
    if (turbineId !== null) q.set("turbine_id", String(turbineId));
    if (typeFilter)         q.set("type", typeFilter);
    api.get<Measurement[]>(`/api/measurements?${q.toString()}`)
      .then(rows => setItems(rows))
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setLoading(false));
    tg()?.HapticFeedback?.impactOccurred("light");
  };

  // Build ordered list of fields the user picked (preserving MEASUREMENT_FIELDS order).
  const chosenOrdered = useMemo(
    () => MEASUREMENT_FIELDS.filter(f => chosenFields.has(f.key)),
    [chosenFields]
  );

  // All visible columns: 4 fixed + user-picked.
  const columns = useMemo<{ key: string; label: string; numeric: boolean }[]>(() => {
    return [
      ...FIXED_COLUMNS.map(c => ({ key: c.key, label: t(c.label_key), numeric: false })),
      ...chosenOrdered.map(f => ({
        key: f.key,
        // Only `single` fields are numeric. `pair` ("X/Y") and `bool`
        // ("on"/"off") are text — no min/max/avg, left-aligned.
        label: f.label + (f.unit ? `, ${f.unit}` : ""),
        numeric: f.kind === "single",
      })),
    ];
  }, [chosenOrdered, t]);

  // Map a measurement row to a flat record keyed by column.
  type Row = Record<string, string | number | null>;
  const rows = useMemo<Row[]>(() => {
    if (!items) return [];
    const turbinesById = new Map(turbines.map(tb => [tb.id, tb.name]));
    return items.map(m => {
      const typeLbl = m.is_emergency ? t("type.emergency")
                    : m.is_offline ? t("type.offline")
                    : m.status === "missed" ? t("type.missed")
                    : m.status === "completed" ? t("type.plan")
                    : t("type.pending");
      const r: Row = {
        _date: m.slot_time.slice(0, 10),
        _slot_time: fmtSlot(m.slot_time),
        _type: typeLbl,
        _turbine: (m.turbine_id !== null && turbinesById.get(m.turbine_id)) || "—",
      };
      for (const f of chosenOrdered) {
        // Decimal columns come back as numbers (we fixed serialization).
        // Pair text columns ("X/Y") come back as strings.
        const v = (m as unknown as Record<string, number | string | null>)[f.key];
        r[f.key] = v ?? null;
      }
      return r;
    });
  }, [items, turbines, chosenOrdered, t]);

  // Sorting
  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const { key, dir } = sort;
    const mul = dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[key], bv = b[key];
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * mul;
      return String(av).localeCompare(String(bv)) * mul;
    });
  }, [rows, sort]);

  const onSort = (key: string) => {
    tg()?.HapticFeedback?.selectionChanged?.();
    setSort(prev => {
      if (prev?.key === key) return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      return { key, dir: "asc" };
    });
  };

  // Footer stats: min / max / avg for each numeric column (only picked ones).
  type Stat = { min: number; max: number; avg: number; n: number };
  const stats = useMemo<Record<string, Stat | null>>(() => {
    const out: Record<string, Stat | null> = {};
    for (const c of columns) {
      if (!c.numeric) { out[c.key] = null; continue; }
      const vals = sortedRows.map(r => r[c.key])
        .filter((v): v is number => typeof v === "number" && Number.isFinite(v));
      if (vals.length === 0) { out[c.key] = null; continue; }
      const sum = vals.reduce((a, b) => a + b, 0);
      out[c.key] = {
        min: Math.min(...vals),
        max: Math.max(...vals),
        avg: sum / vals.length,
        n: vals.length,
      };
    }
    return out;
  }, [columns, sortedRows]);

  const typeOptions: { v: TypeFilter; label: string }[] = [
    { v: "",          label: t("filter.all") },
    { v: "plan",      label: t("type.plan") },
    { v: "emergency", label: t("type.emergency") },
    { v: "offline",   label: t("type.offline") },
  ];

  const fmtCell = (v: string | number | null): string => {
    if (v === null || v === undefined) return "—";
    if (typeof v === "number") return Number.isInteger(v) ? v.toString() : v.toFixed(1);
    return v;
  };

  return (
    <div>
      <h1>{t("custom_preview.title")}</h1>
      <button className="btn secondary" style={{ marginBottom: 12 }}
        onClick={() => navigate(-1)}>← {t("common.back")}</button>

      <div className="card">
        {/* Period */}
        <div style={{ fontSize: 12, color: "var(--hint)", marginBottom: 6 }}>
          {t("custom_preview.period")}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
          <label>
            <div style={{ fontSize: 12, color: "var(--hint)" }}>{t("records.from")}</div>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
          </label>
          <label>
            <div style={{ fontSize: 12, color: "var(--hint)" }}>{t("records.to")}</div>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} />
          </label>
        </div>

        {/* Turbine */}
        {turbines.length > 1 && (
          <>
            <div style={{ fontSize: 12, color: "var(--hint)", marginBottom: 6 }}>
              {t("custom_preview.turbine")}
            </div>
            <div className="period-tabs" style={{ marginBottom: 12 }}>
              <button
                className={"period-tab" + (turbineId === null ? " active" : "")}
                onClick={() => setTurbineId(null)}>{t("filter.all")}</button>
              {turbines.map(tb => (
                <button key={tb.id}
                  className={"period-tab" + (turbineId === tb.id ? " active" : "")}
                  onClick={() => setTurbineId(tb.id)}>{tb.name}</button>
              ))}
            </div>
          </>
        )}

        {/* Type */}
        <div style={{ fontSize: 12, color: "var(--hint)", marginBottom: 6 }}>
          {t("custom_preview.type")}
        </div>
        <div className="period-tabs" style={{ marginBottom: 12 }}>
          {typeOptions.map(o => (
            <button key={o.v}
              className={"period-tab" + (typeFilter === o.v ? " active" : "")}
              onClick={() => setTypeFilter(o.v)}>{o.label}</button>
          ))}
        </div>

        {/* Field picker — collapsible dropdown so 19 items don't crowd the form */}
        <details ref={fieldsRef} className="field-dropdown">
          <summary className="field-dropdown-trigger">
            <span className="field-dropdown-label">
              {t("custom_preview.fields", { n: chosenFields.size, total: MEASUREMENT_FIELDS.length })}
            </span>
            <span className="field-dropdown-arrow">▾</span>
          </summary>
          <div className="field-dropdown-body">
            <div className="field-dropdown-actions">
              <button type="button" className="link-btn" onClick={selectAll}>
                {t("custom_preview.select_all")}
              </button>
              <button type="button" className="link-btn" onClick={clearAll}>
                {t("custom_preview.clear")}
              </button>
            </div>
            {MEASUREMENT_FIELDS.map((f: MeasurementField) => {
              const isOn = chosenFields.has(f.key);
              return (
                <label key={f.key} className={"field-row" + (isOn ? " active" : "")}>
                  <input type="checkbox" checked={isOn} onChange={() => toggleField(f.key)} />
                  <span className="field-row-label">
                    {f.label}{f.unit ? `, ${f.unit}` : ""}
                  </span>
                </label>
              );
            })}
          </div>
        </details>

        <button className="btn preview" style={{ marginTop: 12 }} onClick={build} disabled={loading}>
          {loading ? t("custom_preview.building") : t("custom_preview.build_btn")}
        </button>
      </div>

      {error && <div className="alert error">⚠ {error}</div>}

      {items !== null && (
        <div className="card" style={{ padding: 8, overflowX: "auto" }}>
          {sortedRows.length === 0 ? (
            <div className="empty-state">{t("common.no_data")}</div>
          ) : (
            <table className="custom-table">
              <thead>
                <tr>
                  {columns.map(c => {
                    const isSort = sort?.key === c.key;
                    const arrow = isSort ? (sort!.dir === "asc" ? " ▲" : " ▼") : "";
                    return (
                      <th key={c.key} onClick={() => onSort(c.key)} title={t("custom_preview.sort_hint")}>
                        {c.label}{arrow}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r, idx) => (
                  <tr key={idx}>
                    {columns.map(c => (
                      <td key={c.key} className={c.numeric ? "num" : ""}>{fmtCell(r[c.key])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  {columns.map(c => {
                    const s = stats[c.key];
                    if (!s) return <td key={c.key} />;
                    return (
                      <td key={c.key} className="num stats-cell">
                        <div><span className="stats-tag">min</span> {fmtCell(s.min)}</div>
                        <div><span className="stats-tag">max</span> {fmtCell(s.max)}</div>
                        <div><span className="stats-tag">avg</span> {fmtCell(Math.round(s.avg * 10) / 10)}</div>
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            </table>
          )}
          <div style={{ fontSize: 11, color: "var(--hint)", marginTop: 8, padding: "0 8px" }}>
            {t("custom_preview.rows_count", { n: sortedRows.length })}
          </div>
        </div>
      )}
    </div>
  );
}
